import { useParams, Link, Navigate } from 'react-router';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import { SEOHead } from '@/components/seo/SEOHead';
import { blogPosts } from '@/data/blogPosts';
import { blogContent } from '@/data/blogContent';

export const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();

  // Find the blog post by slug
  const post = blogPosts.find((p) => p.id === slug);

  // If blog post not found, redirect to blog index
  if (!post || !slug) {
    return <Navigate to="/blog" replace />;
  }

  const content = blogContent[slug];

  return (
    <>
      <SEOHead
        title={`${post.title} - PeeTrack Blog`}
        description={post.excerpt}
        keywords={['bladder health', 'fluid tracking', post.category.toLowerCase()]}
        canonical={`/blog/${slug}`}
      />

      <article className="bg-white">
        {/* Hero Section */}
        <section className="bg-gradient-to-b from-cyan-50 to-white py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            {/* Back to Blog Link */}
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold mb-6 group"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Blog
            </Link>

            {/* Post Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-cyan-500 text-white">
                  {post.category}
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {post.title}
              </h1>

              <div className="flex items-center gap-6 text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>{post.readTime}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Blog Content */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            {content || (
              <div className="text-center py-12">
                <p className="text-gray-600">Blog post content is being loaded...</p>
              </div>
            )}
          </div>
        </section>

        {/* Related Posts / CTA */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Ready to Start Tracking?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Join thousands of patients managing their bladder health with PeeTrack
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://app2.peetrack.live/login"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-cyan-500 px-8 py-3 text-lg font-semibold text-white hover:bg-cyan-600 transition-colors shadow-lg"
                >
                  Get Started Free
                </a>
                <a
                  href="https://app2.peetrack.live/login"
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-8 py-3 text-lg font-semibold text-cyan-600 hover:bg-gray-50 transition-colors shadow-lg border-2 border-cyan-200"
                >
                  Try Demo
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Blog */}
        <section className="py-8 bg-white border-t">
          <div className="container mx-auto px-4 max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-cyan-600 hover:text-cyan-700 font-semibold group"
            >
              <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to All Posts
            </Link>
          </div>
        </section>
      </article>
    </>
  );
};
